import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArticleIcon from '@mui/icons-material/Article';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTranslation } from 'react-i18next';
import { blogService } from '../services/blog';
import { styled } from '@mui/material/styles';

const HeroImage = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '400px',
  position: 'relative',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(to bottom, ${alpha(theme.palette.background.default, 0)} 0%, ${theme.palette.background.default} 100%)`
  }
}));

const BlogDetail = () => {
  const { slug } = useParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  useEffect(() => {
    const fetchBlogDetail = async () => {
      try {
        setLoading(true);
        const response = await blogService.getBlogBySlug(slug);
        if (response?.statusCode === 200) {
          setBlog(response.data);
          const relatedResponse = await blogService.getFilteredBlogs(dispatch, {
            filters: [],
            pageIndex: 1,
            pageSize: 3
          });
          if (relatedResponse?.statusCode === 200) {
            setRelatedBlogs(relatedResponse.data.items.filter(item => item.slug !== slug));
          }
        } else {
          setError(response?.message || t('blog.error'));
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogDetail();
  }, [slug, t, dispatch]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 8 }}>
        <Container>
          <Typography color="error" align="center">{error}</Typography>
        </Container>
      </Box>
    );
  }

  if (!blog) return null;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero Section */}
      <HeroImage
        sx={{
          backgroundImage: `url(${blog.featuredImage})`
        }}
      >
        <Container
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            {blog.title}
          </Typography>
          {/* Yazar adı gösterilmez (yazılar Odazen adına yayımlanır) */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon />
              <Typography>
                {t('blog.publishedOn', { date: new Date(blog.createdDate).toLocaleDateString() })}
              </Typography>
            </Box>
          </Box>
        </Container>
      </HeroImage>

      <Container sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 4 }}>
              <Breadcrumbs>
                <Link component={RouterLink} to="/" color="inherit">
                  {t('navigation.home')}
                </Link>
                <Link component={RouterLink} to="/blog" color="inherit">
                  {t('navigation.blog')}
                </Link>
                <Typography color="text.primary">{blog.title}</Typography>
              </Breadcrumbs>
            </Box>

            <Box sx={{ 
              bgcolor: theme.palette.background.paper,
              p: 4,
              borderRadius: 2,
              boxShadow: theme.shadows[1]
            }}>
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, color: 'text.secondary' }}>
                  <VisibilityOutlinedIcon fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {t('blog.viewCount', { count: blog.viewCount })}
                  </Typography>
                </Box>
                <Divider />
              </Box>

              {/* Blog İçeriği */}
              <Box 
                dangerouslySetInnerHTML={{ __html: blog.content }}
                sx={{
                  '& p': { mb: 2, lineHeight: 1.8 },
                  '& h3': { mb: 2, mt: 4, fontWeight: 600 }
                }}
              />

              {/* Etiketler */}
              {blog.tagArray && blog.tagArray.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {t('blog.tags')}
                  </Typography>
                  {blog.tagArray.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      sx={{ mr: 1, mb: 1 }}
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Sidebar - İlgili Bloglar */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 24 }}>
              <Typography variant="h5" sx={{ mb: 3 }}>
                {t('blog.relatedPosts')}
              </Typography>
              {relatedBlogs.map((relatedPost) => (
                <Card 
                  key={relatedPost.id}
                  component={RouterLink}
                  to={`/blog/${relatedPost.slug}`}
                  sx={{
                    mb: 2,
                    textDecoration: 'none',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {relatedPost.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {relatedPost.summary}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default BlogDetail; 